import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { X, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';

const GalleryPage = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [galleryRes, testimonialsRes] = await Promise.all([
          api.getGallery(),
          api.getTestimonials()
        ]);
        setGalleryItems(galleryRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Combine gallery items with testimonial images for carousel
  const carouselImages = [
    ...galleryItems.map(item => ({ ...item, type: 'gallery' })),
    ...testimonials.filter(t => t.image_url).map(t => ({ 
      id: t.id, 
      image_url: t.image_url, 
      title: t.customer_name, 
      description: t.content,
      type: 'testimonial' 
    }))
  ];

  const categories = ['all', ...new Set(galleryItems.map(item => item.category))];
  
  const filteredItems = filter === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === filter);

  // Auto-advance carousel
  useEffect(() => {
    if (carouselImages.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % carouselImages.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + carouselImages.length) % carouselImages.length);

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="gallery-page">
      {/* Hero Section with Carousel */}
      <section className="py-16 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
              Our Work
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 mb-6">
              Real Results
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto">
              See the quality of our work and hear from our satisfied customers across Vancouver Island.
            </p>
          </motion.div>

          {/* Image Carousel */}
          {carouselImages.length > 0 && (
            <div className="relative aspect-[21/9] rounded-lg overflow-hidden mb-8">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentSlide}
                  src={carouselImages[currentSlide]?.image_url}
                  alt={carouselImages[currentSlide]?.title}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Carousel Controls */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-electric-teal hover:text-primary-dark transition-all"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-electric-teal hover:text-primary-dark transition-all"
                data-testid="carousel-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Info */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-white font-bold text-xl">{carouselImages[currentSlide]?.title}</h3>
                {carouselImages[currentSlide]?.description && (
                  <p className="text-white/80 text-sm mt-1 line-clamp-2">
                    {carouselImages[currentSlide]?.description}
                  </p>
                )}
              </div>

              {/* Carousel Dots */}
              <div className="absolute bottom-4 right-4 flex gap-1">
                {carouselImages.slice(0, 10).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentSlide ? 'bg-electric-teal w-6' : 'bg-white/50'
                    }`}
                  />
                ))}
                {carouselImages.length > 10 && (
                  <span className="text-white/50 text-xs ml-2">+{carouselImages.length - 10}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials with Photos Section */}
      <section className="py-16 bg-primary-dark" data-testid="testimonials-with-photos">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8">What Our Clients Say</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-secondary-dark rounded-lg overflow-hidden border border-white/10"
                data-testid={`testimonial-card-${index}`}
              >
                {testimonial.image_url && (
                  <div className="aspect-video relative">
                    <img 
                      src={testimonial.image_url}
                      alt={`Work for ${testimonial.customer_name}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary-dark to-transparent" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-electric-teal text-electric-teal" />
                    ))}
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed mb-4">
                    "{testimonial.content}"
                  </p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-white font-semibold">{testimonial.customer_name}</p>
                    <p className="text-white/50 text-sm">{testimonial.customer_location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery Section */}
      <section className="py-16 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8">Project Gallery</h2>
          
          {/* Filter Bar */}
          <div className="flex flex-wrap gap-3 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
                  filter === category
                    ? 'bg-electric-teal text-primary-dark'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`filter-${category}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/60 text-lg">No projects found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`gallery-item-${index}`}
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
                    <img 
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute inset-0 border border-white/0 group-hover:border-electric-teal/50 transition-all duration-500 rounded-lg" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <span className="text-electric-teal text-sm font-bold uppercase tracking-wider">
                        {item.category}
                      </span>
                      <h3 className="text-white font-bold text-lg mt-1">
                        {item.title}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
          data-testid="lightbox"
        >
          <button 
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            onClick={() => setSelectedItem(null)}
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
          <div 
            className="max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedItem.image_url}
              alt={selectedItem.title}
              className="w-full rounded-lg"
            />
            <div className="mt-4">
              <span className="text-electric-teal text-sm font-bold uppercase tracking-wider">
                {selectedItem.category}
              </span>
              <h3 className="text-white font-bold text-2xl mt-1">
                {selectedItem.title}
              </h3>
              <p className="text-white/70 mt-2">
                {selectedItem.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
